import { R } from '@/features/booking/resources/Texts.resx';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    bookingDraftAtom,
    bookingErrorAtom,
    SessionStorageKeys,
    updateDraftAtom,
} from '@/features/booking/shared/model/bookingState';
import { IFileDto } from '@/shared/interfaces';
import { useAtomValue, useSetAtom } from 'jotai';

export enum ErrorCode {
    FileTooLarge = 'file-too-large',
    DuplicateName = 'duplicate-name',
}

type UploadFile = {
    uid?: string;
    file?: File;
    name: string;
    previewUrl?: string;
    progress: number;
};

const MAX_FILES = 10;
const MAX_SIZE_BYTES = 30_000_000;

const isTemporaryId = (uid: string) => uid.startsWith('temp-');

export const useAttachments = ({
    onChange,
}: {
    onChange: (uids: string[], uploadInProgress: boolean) => void;
}) => {
    const setErrorMessage = useSetAtom(bookingErrorAtom);
    const draftState = useAtomValue(bookingDraftAtom);
    const updateDraftState = useSetAtom(updateDraftAtom);
    const [files, setFiles] = useState<UploadFile[]>([]);
    const [fileInFocus, setFileInFocus] = useState(null);
    const [hasLoadedFiles, setHasLoadedFiles] = useState(false);

    const uploadFile = (uploadFile: UploadFile, temporaryId: string) => {
        if (!uploadFile?.file) return;

        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        formData.append('file', uploadFile.file);

        xhr.open('POST', '/api/files/upload');

        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;

            const percent = Math.round((event.loaded / event.total) * 100);

            setFiles((prev) =>
                prev.map((f) =>
                    f.uid === temporaryId ? { ...f, progress: percent } : f,
                ),
            );
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                const uid = JSON.parse(xhr.responseText);
                const updatedFile = { ...uploadFile, uid: uid, progress: 100 };

                setFiles((prev) => {
                    const updated: UploadFile[] = prev.map((f) => {
                        return f.uid === temporaryId ? updatedFile : f;
                    });
                    return updated;
                });
            } else {
                removeFile(temporaryId);
                const isPlainText = xhr
                    .getResponseHeader('Content-Type')
                    ?.toLowerCase()
                    .startsWith('text/plain');
                setErrorMessage(
                    (isPlainText && xhr.responseText) ||
                        R.AttachmentUploadFailed,
                );
            }
        };

        xhr.onerror = () => {
            removeFile(temporaryId);
            setErrorMessage(R.AttachmentUploadFailed);
        };

        xhr.send(formData);
    };

    // this effect is responsible for calling onChange whenever files are added/removed or their upload progress changes
    // it filters out files with temporary ids (uid in [0...9]) to ensure onChange is only called with fully uploaded files
    useEffect(() => {
        if (!draftState || !hasLoadedFiles) return;

        onChange(
            files.filter((f) => !isTemporaryId(f.uid)).map((f) => f.uid),
            files.some((f) => f.progress < 100),
        );
    }, [files, hasLoadedFiles]);

    // fetch attachments according once draftState is hydrated (handles refresh)
    useEffect(() => {
        const attachmentUids = draftState?.attachmentUids;
        if (!attachmentUids || hasLoadedFiles) return;

        // this is an extra precaution to ensure we don't try to fetch files with temporary ids
        const filteredUids = attachmentUids
            .split(',')
            .filter((uid) => uid && !isTemporaryId(uid));

        if (!filteredUids.length) return;

        const loadFiles = async () => {
            const res = await fetch(
                `/api/files/attachments?${SessionStorageKeys.AttachmentUids}=${filteredUids.join(',')}`,
            );

            const json = await res.json();
            const data: IFileDto[] = Array.isArray(json) ? json : [];

            if (!data.length) {
                setHasLoadedFiles(true);
                return;
            }

            const existing: UploadFile[] = data?.map((f) => ({
                uid: f.UID,
                name: f.FileName,
                previewUrl: `/api/files/${f.UID}`,
                progress: 100,
            }));

            setFilesWithSimulatedProgress(existing, setFiles);
            setHasLoadedFiles(true);
        };

        void loadFiles();
    }, [draftState?.attachmentUids, hasLoadedFiles]);

    const onDrop = (acceptedFiles: File[]) => {
        setHasLoadedFiles(true);

        const remaining = MAX_FILES - (files.length + acceptedFiles.length);

        if (remaining < 0) {
            acceptedFiles = acceptedFiles.slice(0, remaining);
        }

        const newFiles: UploadFile[] = acceptedFiles.map((file, index) => ({
            uid: `temp-${index}`,
            file: file,
            name: file.name,
            previewUrl: URL.createObjectURL(file),
            progress: 0,
        }));

        setFiles((prev) => {
            const updated: UploadFile[] = [...prev, ...newFiles];
            return updated;
        });

        newFiles.forEach((file) => uploadFile(file, file.uid));
    };

    const fileValidator = (file: File) => {
        if (files.map((f) => f.name).includes(file.name)) {
            return {
                code: ErrorCode.DuplicateName,
                message: R.DuplicateNameErrorMessage,
            };
        }

        return null;
    };

    const { getRootProps, getInputProps, open, fileRejections } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/gif': ['.gif'],
            'application/pdf': [],
            'video/quicktime': [],
            'video/mp4': ['.mp4'],
        },
        maxSize: MAX_SIZE_BYTES, // 30mb
        onDrop,
        noDrag: true,
        validator: fileValidator,
    });

    const removeFile = (uid?: string) => {
        setFiles((prev) => {
            const toRemove = prev.find((f) => f.uid === uid);

            if (toRemove?.previewUrl) {
                URL.revokeObjectURL(toRemove.previewUrl);
            }

            return prev.filter((f) => f.uid !== uid);
        });
    };

    const setFilesWithSimulatedProgress = (
        files: UploadFile[],
        setFiles: (files: UploadFile[]) => void,
    ) => {
        const updatedFiles = files.map((f) => ({ ...f, progress: 0 }));
        setFiles(updatedFiles);

        const steps = [
            [0, 2],
            [20, 40],
            [40, 60],
            [60, 80],
            [80, 100],
        ];

        let stepIndex = 0;

        const interval = setInterval(() => {
            if (stepIndex >= steps.length) {
                clearInterval(interval);
                return;
            }

            const [min, max] = steps[stepIndex];

            const newFiles = updatedFiles.map((f) => {
                if (stepIndex === steps.length - 1) {
                    return { ...f, progress: 100 };
                } else {
                    const rand =
                        Math.floor(Math.random() * (max - min + 1)) + min;
                    return { ...f, progress: rand };
                }
            });

            setFiles(newFiles);
            stepIndex++;
        }, 100);
    };

    const handleButtonClick = () => {
        if (!draftState) {
            updateDraftState({ attachmentUids: '' });
        }
        open();
    };

    return {
        files,
        fileRejections,
        getRootProps,
        getInputProps,
        removeFile,
        handleButtonClick,
        fileInFocus,
        setFileInFocus,
    };
};
